import { FiAlertTriangle } from 'react-icons/fi';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import CurrencyInput from '../../../molecules/CurrencyInput';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import {
    SEIZED_ASSET_STATUS_OPTIONS,
    SeizedAssetStatus,
    SeizedAssetType,
    type SelectOption,
} from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseSeizedAssetForm, {
    IDENTIFICATION_FIELDS_BY_TYPE,
    IDENTIFICATION_FIELD_LABELS,
    UF_OPTIONS,
    residualErrors,
} from '../../../../hooks/useLegalCaseSeizedAssetForm';
import type { AddSeizedAssetValue } from '../../../../hooks/useLegalCaseSeizedAssets';

// Um campo de identificação. UF é um select (lista de estados); os demais são texto livre.
// Extraído para fora do componente (diretriz: sem função dentro de função).
const IdentificationField = ({
    name,
    value,
    errorMessage,
    onChange,
}: {
    name: string;
    value: string;
    errorMessage?: string;
    onChange: (value: string) => void;
}) => {
    if (name === 'state') {
        return (
            <SelectTextField
                label={IDENTIFICATION_FIELD_LABELS[name]}
                items={UF_OPTIONS}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                errorMessage={errorMessage}
            />
        );
    }
    return (
        <TextEdit
            label={IDENTIFICATION_FIELD_LABELS[name]}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            errorMessage={errorMessage}
        />
    );
};

interface LegalCaseSeizedAssetFormProps {
    typeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: AddSeizedAssetValue) => Promise<boolean>;
    onCancel: () => void;
}

// Só JSX + bindings: estado e regras vivem em useLegalCaseSeizedAssetForm. Os campos de
// identificação são renderizados conforme o tipo do bem (a família do assetType escolhido).
const LegalCaseSeizedAssetForm = ({
    typeOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCaseSeizedAssetFormProps) => {
    const form = useLegalCaseSeizedAssetForm({ onSubmit });
    const identificationFields = IDENTIFICATION_FIELDS_BY_TYPE[form.assetType] || [];

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SelectTextField
                    label="Tipo do bem"
                    items={typeOptions}
                    value={form.assetType}
                    onChange={(e) => form.setAssetType(e.target.value as SeizedAssetType)}
                    errorMessage={getErrorMessage(errorFields, 'assetType')}
                />
                <SelectTextField
                    label="Status"
                    items={SEIZED_ASSET_STATUS_OPTIONS}
                    value={form.status}
                    onChange={(e) => form.setStatus(e.target.value as SeizedAssetStatus)}
                    errorMessage={getErrorMessage(errorFields, 'status')}
                />
            </div>

            {/* Identificação por tipo (veículo: placa/RENAVAM/...; imóvel: matrícula/...). */}
            {identificationFields.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {identificationFields.map((name) => (
                        <IdentificationField
                            key={name}
                            name={name}
                            value={form.identification[name] || ''}
                            errorMessage={getErrorMessage(errorFields, name)}
                            onChange={(value) => form.setIdentificationField(name, value)}
                        />
                    ))}
                </div>
            )}

            <TextEdit
                label="Descrição do bem"
                value={form.description}
                onChange={(e) => form.setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextEdit
                    label="Grau da penhora (opcional)"
                    value={form.grade}
                    onChange={(e) => form.setGrade(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'grade')}
                />
                <TextEdit
                    label="Credor concorrente (opcional)"
                    value={form.competingCreditor}
                    onChange={(e) => form.setCompetingCreditor(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'competingCreditor')}
                />
                <CurrencyInput
                    label="Valor estimado (opcional)"
                    prefix="R$"
                    fractionDigits={2}
                    value={form.estimatedValue}
                    onChange={form.setEstimatedValue}
                    errorMessage={getErrorMessage(errorFields, 'estimatedValue')}
                />
                <CurrencyInput
                    label="Valor de avaliação (opcional)"
                    prefix="R$"
                    fractionDigits={2}
                    value={form.appraisedValue}
                    onChange={form.setAppraisedValue}
                    errorMessage={getErrorMessage(errorFields, 'appraisedValue')}
                />
            </div>

            {/* Erro de identificação mínima (bem sem nenhum dado nem descrição) e outros 422
                sem campo próprio — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Registrar bem'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseSeizedAssetForm;
